import CryptoJS from 'crypto-js';

const APP_CIPHER_SALT = process.env.REACT_APP_CIPHER_SALT || 'default-salt-value-if-not-set';

export interface UserCredentials {
  username: string;
  password: string;
  countryId: number;
  language: string;
  rememberLogin: boolean;
  tosAgree: boolean;
}

/**
 * Criptografa as credenciais do usuário
 * @param credentials Credenciais do usuário
 * @returns String criptografada
 */
export function encryptCredentials(credentials: UserCredentials): string {
  try {
    const credentialsString = JSON.stringify(credentials);
    const encrypted = CryptoJS.AES.encrypt(credentialsString, APP_CIPHER_SALT).toString();
    return encrypted;
  } catch (error) {
    console.error('Erro ao criptografar credenciais:', error);
    throw new Error('Falha na criptografia das credenciais');
  }
}

/**
 * Descriptografa as credenciais do usuário
 * @param encryptedCredentials String criptografada
 * @returns Credenciais descriptografadas ou null se houver erro
 */
export function decryptCredentials(encryptedCredentials: string): UserCredentials | null {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedCredentials, APP_CIPHER_SALT);
    const decryptedString = bytes.toString(CryptoJS.enc.Utf8);
    
    if (!decryptedString) {
      return null;
    }
    
    const credentials = JSON.parse(decryptedString) as UserCredentials;
    return credentials;
  } catch (error) {
    console.error('Erro ao descriptografar credenciais:', error);
    return null;
  }
}

/**
 * Salva credenciais criptografadas no storage apropriado
 * @param credentials Credenciais do usuário
 */
export function saveEncryptedCredentials(credentials: UserCredentials): void {
  try {
    const encryptedCredentials = encryptCredentials(credentials);
    
    if (credentials.rememberLogin) {
      localStorage.setItem('encryptedCredentials', encryptedCredentials);
      sessionStorage.removeItem('encryptedCredentials');
    } else {
      sessionStorage.setItem('encryptedCredentials', encryptedCredentials);
      localStorage.removeItem('encryptedCredentials');
    }
  } catch (error) {
    console.error('Erro ao salvar credenciais criptografadas:', error);
  }
}

/**
 * Recupera e descriptografa credenciais do storage
 * @returns Credenciais descriptografadas ou null
 */
export function getDecryptedCredentials(): UserCredentials | null {
  try {
    // Verifica primeiro o localStorage (para "manter conectado")
    let encryptedCredentials = localStorage.getItem('encryptedCredentials');
    
    // Se não encontrou no localStorage, verifica o sessionStorage
    if (!encryptedCredentials) {
      encryptedCredentials = sessionStorage.getItem('encryptedCredentials');
    }
    
    if (!encryptedCredentials) {
      return null;
    }
    
    return decryptCredentials(encryptedCredentials);
  } catch (error) {
    console.error('Erro ao recuperar credenciais criptografadas:', error);
    return null;
  }
}

/**
 * Remove credenciais criptografadas de ambos os storages
 */
export function clearEncryptedCredentials(): void {
  try {
   localStorage.removeItem('encryptedCredentials');
    sessionStorage.removeItem('encryptedCredentials');
  } catch (error) {
    console.error('Erro ao limpar credenciais criptografadas:', error);
  }
}

/**
 * Verifica se existem credenciais salvas
 * @returns true se existem credenciais salvas
 */
export function hasStoredCredentials(): boolean {
  const localStorage_credentials = localStorage.getItem('encryptedCredentials');
  const sessionStorage_credentials = sessionStorage.getItem('encryptedCredentials');
  
  return !!(localStorage_credentials || sessionStorage_credentials);
}